import { useState } from "react";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { isAllowedNotification, useNotifications } from "../../../services/shared/notifications";
import { useAuth } from "../../../context/AuthContext";

// ---------------------------------------------------------------------------
// NotificationBell
//
// Renders the bell icon badge in TopNavigation.
// On click, opens a Popover showing the 10 most recent notifications.
// Displays unread count badge and allows mark-read / mark-all-read.
// Links to the full Notification Center page.
// ---------------------------------------------------------------------------

const NotificationBell = () => {
  const { profile } = useAuth();
  const {
    unreadCount,
    items,
    loadingItems,
    loadItems,
    onMarkRead,
    onMarkAllRead,
  } = useNotifications(profile?.id);

  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const navigate = useNavigate();

  const open = Boolean(anchor);

  // Defense-in-depth: useNotifications already filters items/unreadCount
  // against the allow-list (UAT PR-2), but re-applying it here means this
  // component never renders an excluded type even if a future caller of the
  // hook forgets to filter upstream.
  const visibleItems = items.filter(isAllowedNotification);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchor(e.currentTarget);
    loadItems(10);
  };

  const handleClose = () => setAnchor(null);

  const handleNotificationClick = async (id: string, link?: string) => {
    await onMarkRead(id);
    if (link) {
      handleClose();
      navigate(link);
    }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton size="large" aria-label="notifications" onClick={handleOpen}>
          <Badge badgeContent={unreadCount > 99 ? "99+" : unreadCount || undefined} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 360, maxHeight: 480, display: "flex", flexDirection: "column" } } }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.5 }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Notifications
            {unreadCount > 0 && (
              <Typography
                component="span"
                variant="caption"
                sx={{ ml: 1, color: "text.secondary" }}
              >
                ({unreadCount} unread)
              </Typography>
            )}
          </Typography>
          {unreadCount > 0 && (
            <Tooltip title="Mark all as read">
              <IconButton size="small" onClick={onMarkAllRead}>
                <DoneAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Divider />

        {/* List */}
        <Box sx={{ overflowY: "auto", flex: 1 }}>
          {loadingItems ? (
            <Stack alignItems="center" py={3}>
              <CircularProgress size={24} />
            </Stack>
          ) : visibleItems.length === 0 ? (
            <Stack alignItems="center" py={4} spacing={1}>
              <NotificationsIcon sx={{ color: "text.disabled", fontSize: 40 }} />
              <Typography variant="body2" color="text.secondary">
                No notifications yet
              </Typography>
            </Stack>
          ) : (
            <List disablePadding>
              {visibleItems.map((n) => (
                <ListItem
                  key={n.id}
                  alignItems="flex-start"
                  onClick={() => handleNotificationClick(n.id, n.link)}
                  sx={{
                    cursor: n.link ? "pointer" : "default",
                    bgcolor: n.read_at ? "transparent" : "action.hover",
                    "&:hover": { bgcolor: "action.selected" },
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    py: 1.25,
                    px: 2,
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        fontWeight={n.read_at ? 400 : 600}
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {n.title}
                      </Typography>
                    }
                    secondary={
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.25}>
                        {n.body && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              mr: 1,
                            }}
                          >
                            {n.body}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
                          {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                        </Typography>
                      </Stack>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* Footer */}
        <Divider />
        <Box sx={{ px: 2, py: 1 }}>
          <Button
            fullWidth
            size="small"
            variant="text"
            onClick={() => { handleClose(); navigate("/notifications"); }}
          >
            View all notifications
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
